import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Calendar/Calendar.css';
import './../../App.css';
import { CalendarProvider, useCalendar } from '../../contexts/CalendarContext';
import Calendar from '../Calendar/Calendar';
import ListViewAllBookings from '../SetCastUser/ListViewAllBookings';
import { websocketService } from '../../services/websocketService';

/**
 * AllBookingsPage component using the custom Calendar component
 * This component displays all bookings in a calendar view using the API
 */
const AllBookingsPageContent = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('calendar');
  // The order-type filter lives in the Calendar's own filter bar (Calendar.js) for the calendar
  // view and in ListViewAllBookings for the list view.
  const { events, setEvents, currentDate } = useCalendar();

  // Helper function to get current week number (ISO 8601)
  const getCurrentWeek = (date = new Date()) => {
    const targetDate = new Date(date);
    const start = new Date(targetDate.getFullYear(), 0, 1);
    const today = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    // Set both dates to Thursday of their respective weeks
    // This ensures we get the correct ISO week number
    start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
    today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
    
    const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
    return weekNumber;
  };

  // Get initial week from calendar's current date or current date
  const getInitialWeek = () => {
    if (currentDate) {
      return getCurrentWeek(currentDate);
    }
    return getCurrentWeek();
  };

  // Initialize WebSocket connection
  useEffect(() => {
    websocketService.connect();

    // Subscribe to booking updates
    const handleBookingUpdate = (data) => {
      // console.log('Received booking update:', data);
      setEvents(prevEvents => {
        // Find and update the existing event if it exists
        const eventIndex = prevEvents.findIndex(event => event.id === data.id);
        if (eventIndex !== -1) {
          const updatedEvents = [...prevEvents];
          updatedEvents[eventIndex] = { ...updatedEvents[eventIndex], ...data };
          return updatedEvents;
        }
        // Add new event if it doesn't exist
        return [...prevEvents, data];
      });
    };

    websocketService.subscribe('booking', handleBookingUpdate);

    // Cleanup on unmount
    return () => {
      websocketService.unsubscribe('booking', handleBookingUpdate);
      websocketService.disconnect();
    };
  }, [setEvents]);

  // Handle event click to navigate to the booking details
  const handleEventClick = (event) => {
    if (event && event.id) {
      navigate(`/admin/detail?id=${event.id}`);
    }
  };

  // Switch between calendar and list views
  const switchView = (viewType) => {
    setView(viewType);
  };

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper" style={{ flex: 1, overflowY: 'auto', paddingBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '60px' }}>
          <h1>All Bookings</h1>
          <div>
            <a href='allgroups'><button className='big-button'>Groups</button></a>
            <button 
              className={view === 'calendar' ? 'big-button' : 'big-button-active'} 
              onClick={() => switchView('list')} 
              style={{ marginLeft: 'auto' }}
            >
              List View
            </button>
            <button 
              className={view === 'calendar' ? 'big-button-active' : 'big-button'}
              onClick={() => switchView('calendar')}
            >
              Calendar View
            </button>
          </div>        
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading calendar...
          </div>
        )}

        {view === 'calendar' ? (
          <Calendar
            onEventClick={handleEventClick}
            onDateSelect={(date) => console.log('Date selected:', date)}
          />
        ) : (
          <ListViewAllBookings 
            events={events || []} 
            initialWeek={getInitialWeek()}
          />
        )}
      </div>
    </div>
  );
};

// Wrap the component with CalendarProvider to make context available
const AllBookingsPage = () => (
  <CalendarProvider>
    <AllBookingsPageContent />
  </CalendarProvider>
);

export default AllBookingsPage;